import {
  Alert,
  Box,
  Button,
  DataTable,
  IconButton,
  IconTrash,
  Loader,
  Text,
} from '@tocktix/pantry';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { ResolveUsersResponse } from 'tools/bff/generated/user/user_administration_pb';
import { userClient } from 'tools/bff/client';

// A resolved user that carries a GDUS uuid, i.e. one the bulk deactivation can actually act on.
export interface DeactivatableUser {
  gdusUuid: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  isLockedOut: boolean;
}

interface PreviewRow extends DeactivatableUser {
  index: number;
}

const EM_DASH = '\u2014';

const formatName = (row: PreviewRow): string => {
  const name = `${row.firstName} ${row.lastName}`.trim();
  return name || EM_DASH;
};

interface BulkUserPreviewTableProps {
  identifiers: string[];
  onDeactivatableChange: (users: DeactivatableUser[]) => void;
}

const BulkUserPreviewTable: FunctionComponent<BulkUserPreviewTableProps> = ({
  identifiers,
  onDeactivatableChange,
}) => {
  const [response, setResponse] = useState<ResolveUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    setRemoved(new Set());
    userClient
      .resolveUsers({ identifiers })
      .then((res) => {
        if (mounted) {
          setResponse(res);
        }
      })
      .catch(() => {
        if (mounted) {
          setError('Failed to resolve identifiers. Please try again.');
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [identifiers]);

  const rows: PreviewRow[] = useMemo(
    () =>
      (response?.resolved ?? []).map((user, index) => {
        const gdus = user.gdusUser;
        return {
          index,
          gdusUuid: gdus?.gdusUuid ?? '',
          email: gdus?.email ?? '',
          firstName: gdus?.firstName ?? '',
          lastName: gdus?.lastName ?? '',
          phoneNumber: gdus?.phoneNumber ?? '',
          isLockedOut: gdus?.isLockedOut ?? false,
        };
      }),
    [response]
  );

  // A resolved match with no GDUS uuid (Resy/Tock-only) has no identity to lock out.
  const noUuidCount = useMemo(() => rows.filter((row) => !row.gdusUuid).length, [rows]);

  const visibleRows = useMemo(
    () => rows.filter((row) => row.gdusUuid && !removed.has(row.gdusUuid)),
    [rows, removed]
  );

  useEffect(() => {
    onDeactivatableChange(
      visibleRows.map(({ gdusUuid, email, firstName, lastName, phoneNumber, isLockedOut }) => ({
        gdusUuid,
        email,
        firstName,
        lastName,
        phoneNumber,
        isLockedOut,
      }))
    );
  }, [visibleRows, onDeactivatableChange]);

  const handleRemove = useCallback((gdusUuid: string) => {
    setRemoved((prev) => new Set(prev).add(gdusUuid));
  }, []);

  if (loading) {
    return (
      <Box padding={300} display="flex" justifyContent="center">
        <Loader aria-label="Resolving identifiers" size="md" />
      </Box>
    );
  }

  if (error) {
    return <Alert color="danger" message={error} data-testid="bulk-user-resolve-error" />;
  }

  const unresolved = response?.unresolved ?? [];

  return (
    <Box display="flex" flexDirection="column" gap={300}>
      {unresolved.length > 0 && (
        <Alert
          color="warning"
          title={`${unresolved.length} identifier(s) did not match any user`}
          message={unresolved.join(', ')}
          data-testid="bulk-user-unresolved-callout"
        />
      )}
      {noUuidCount > 0 && (
        <Alert
          color="warning"
          title={`${noUuidCount} resolved user(s) cannot be deactivated`}
          message="These matched a Resy/Tock profile with no Global Dining identity, so there is no account to lock out. They are excluded from this batch."
          data-testid="bulk-user-no-uuid-callout"
        />
      )}
      {removed.size > 0 && (
        <Box display="flex" alignItems="center" gap={200}>
          <Text variant="caption" color="fgSubtle">
            {removed.size} row(s) removed from this batch.
          </Text>
          <Button
            variant="text"
            label="Undo removals"
            onClick={() => setRemoved(new Set())}
            data-testid="bulk-user-undo-removals-button"
          />
        </Box>
      )}
      {visibleRows.length === 0 ? (
        <Text variant="body" color="fgSubtle" data-testid="bulk-user-preview-empty">
          No users available to deactivate.
        </Text>
      ) : (
        <DataTable id="bulk-user-preview-table">
          <DataTable.HeaderRow>
            <DataTable.Header id="name" title="Name" />
            <DataTable.Header id="email" title="Email" />
            <DataTable.Header id="phone" title="Phone" />
            <DataTable.Header id="status" title="Status" />
            <DataTable.Header id="actions" title="" />
          </DataTable.HeaderRow>
          <DataTable.Body>
            {visibleRows.map((row) => (
              <DataTable.Row key={row.gdusUuid} id={row.index} index={row.index}>
                <DataTable.Cell columnId="name">{formatName(row)}</DataTable.Cell>
                <DataTable.Cell columnId="email">{row.email || EM_DASH}</DataTable.Cell>
                <DataTable.Cell columnId="phone">{row.phoneNumber || EM_DASH}</DataTable.Cell>
                <DataTable.Cell columnId="status">
                  {row.isLockedOut ? (
                    <Text variant="bodySmStrong" color="fgWarningBase">
                      Already locked out
                    </Text>
                  ) : (
                    <Text variant="bodySm" color="fgSubtle">
                      Active
                    </Text>
                  )}
                </DataTable.Cell>
                <DataTable.Cell columnId="actions">
                  <IconButton
                    Icon={IconTrash}
                    aria-label={`Remove ${row.email || formatName(row)}`}
                    onClick={() => handleRemove(row.gdusUuid)}
                    data-testid={`bulk-user-remove-${row.gdusUuid}`}
                  />
                </DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable.Body>
        </DataTable>
      )}
    </Box>
  );
};

export default BulkUserPreviewTable;
